import {
  flightSearchEnum,
  getMaxSearchDate,
  isOutsideSearchWindow,
  useAppForm,
} from '@massilia/features'
import { Button, cn, useIsMobile } from '@massilia/ui'
import { standardSchemaValidators } from '@tanstack/react-form'
import { add, addDays, format, isBefore, min, startOfDay } from 'date-fns'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'

import type { FlightSearchSchemaType } from '@/lib/schemas'

import {
  DEFAULT_FLIGHT_LET,
  DEFAULT_OPTION,
  DEFAULT_VALUES,
  flightSearchSchema,
  travelClassTypesLabels,
} from '@/lib/schemas'

import { FlightDateWarningModal } from '../flight-date-warning-modal'
import { RoundTripContinuityModal } from '../round-trip-continuity-modal'
import { FlightLegRowFormGroup } from './flight-leg-row-form-group'
import { PassengersFormGroup } from './passengers-form-group'

const WIDGET_BASE_ORIGIN_KEY = '__MASSILIA_WIDGET_BASE_ORIGIN__'

const FIELD_LABEL = 'text-gray-light-600 text-[11px] font-semibold tracking-wide uppercase'

// Shorter labels for the mobile segmented control (full names don't fit three-up).
const MOBILE_TYPE_LABELS: Record<string, string> = {
  'multi-destination': 'Multi',
  'one-way': 'Aller simple',
  'round-trip': 'Aller-retour',
}

// Date par défaut du segment suivant : J+1 après le précédent, sans dépasser l'horizon de recherche.
const nextLegDate = (previousDate: Date | undefined) =>
  min([add(previousDate ?? new Date(), { days: 1 }), getMaxSearchDate()])

interface FlightSearchFormProps {
  errorPopoverSide?: 'bottom' | 'top'
  errorsDisplayType?: 'bellow' | 'popover'
  initialValues?: FlightSearchSchemaType
  useWordpressDialog?: boolean
}

export function FlightSearchForm({
  errorPopoverSide = 'bottom',
  errorsDisplayType = 'popover',
  initialValues,
  useWordpressDialog = false,
}: FlightSearchFormProps) {
  // const [isContinuityModalOpen, setIsContinuityModalOpen] = useState(false)
  const [isDateWarningModalOpen, setIsDateWarningModalOpen] = useState(false)
  const isMobile = useIsMobile()

  const form = useAppForm({
    defaultValues: initialValues ?? DEFAULT_VALUES,
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === 'type') {
          const newType = formApi.getFieldValue('type')
          const flights = formApi.getFieldValue('flight')

          if (['one-way', 'round-trip'].includes(newType)) {
            formApi.setFieldValue(
              'flight',
              flights.slice(0, 1).map((f) => ({
                ...f,
                date: { from: newType === 'one-way' ? new Date() : undefined },
              }))
            )
          } else {
            formApi.setFieldValue('flight', [
              ...[{ ...flights.at(-1)!, date: { from: new Date() } }],
              {
                date: {
                  from: nextLegDate(flights.at(-1)?.date.from),
                },
                departure: DEFAULT_OPTION,
                destination: DEFAULT_OPTION,
              },
            ])
          }
          //NOTE: Resets errors as validation paths aren't the same on every type
          formApi.setErrorMap({
            onSubmit: {
              fields: {},
            },
          })
        }
      },
    },
    onSubmit: ({ value }) => {
      const serializedValue = {
        flight: value.flight.map((flight) => ({
          date: {
            from: flight.date.from ? format(flight.date.from, 'yyyy-MM-dd') : undefined,
            to: flight.date.to ? format(flight.date.to, 'yyyy-MM-dd') : undefined,
          },
          departure: {
            category: flight.departure.category,
            label: flight.departure.label,
            value: flight.departure.value,
          },
          destination: {
            category: flight.destination.category,
            label: flight.destination.label,
            value: flight.destination.value,
          },
        })),
        passengers: {
          adults: value.passengers.adults,
          babies: value.passengers.babies,
          infants: value.passengers.infants,
          seniors: value.passengers.seniors,
          youngsters: value.passengers.youngsters,
        },
        travelClass: value.travelClass,
        type: value.type,
        withLuggage: value.withLuggage,
      }

      const widgetBaseOrigin =
        (window as Window & { [WIDGET_BASE_ORIGIN_KEY]?: string })[WIDGET_BASE_ORIGIN_KEY] ??
        window.location.origin
      const resultsUrl = new URL('/vols/resultats', widgetBaseOrigin)
      resultsUrl.searchParams.set('flightSearch', JSON.stringify(serializedValue))
      window.location.assign(resultsUrl.toString())
    },
    validators: {
      onSubmit: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, flightSearchSchema),
    },
  })

  const pushFlight = () => {
    form.pushFieldValue('flight', {
      ...DEFAULT_FLIGHT_LET,
      date: {
        from: nextLegDate(form.state.values.flight.at(-1)?.date.from),
      },
    })
  }

  // Shared between layouts: the leg rows adapt to mobile/desktop internally.
  const legsArray = (type: FlightSearchSchemaType['type']) => (
    <form.AppField mode="array" name="flight">
      {(field) => (
        <>
          {field.state.value.map((_, i) => {
            const removable = type === 'multi-destination' && field.state.value.length > 2
            return (
              <FlightLegRowFormGroup
                dateType={type === 'round-trip' ? 'range' : 'single'}
                disabledDateFn={(date) => {
                  const previousDate = field.form.getFieldValue(`flight[${i - 1}].date.from`)
                  return (previousDate && date < previousDate) || isOutsideSearchWindow(date)
                }}
                errorPopoverSide={errorPopoverSide}
                errorsDisplayType={errorsDisplayType}
                fields={{
                  date: `flight[${i}].date`,
                  departure: `flight[${i}].departure`,
                  destination: `flight[${i}].destination`,
                }}
                form={form}
                key={i}
                multiLeg={type === 'multi-destination'}
                onRemove={
                  removable
                    ? () => {
                        field.removeValue(i)
                      }
                    : undefined
                }
              />
            )
          })}
        </>
      )}
    </form.AppField>
  )

  const passengersField = (
    <form.AppField name="passengers">
      {() => (
        <PassengersFormGroup
          fields={{
            adults: 'passengers.adults',
            babies: 'passengers.babies',
            infants: 'passengers.infants',
            seniors: 'passengers.seniors',
            youngsters: 'passengers.youngsters',
          }}
          form={form}
        />
      )}
    </form.AppField>
  )

  const travelClassField = (
    <form.AppField name="travelClass">
      {(field) => (
        <field.SelectField
          options={Object.entries(travelClassTypesLabels).map(([key, value]) => ({
            label: value,
            value: key,
          }))}
          overlayOnMobile
        />
      )}
    </form.AppField>
  )

  const searchButton = (
    <Button
      className="bg-brand-600 hover:bg-brand-800 h-12 w-full justify-center text-base font-semibold text-white"
      type="submit"
    >
      <Search className="size-4" />
      Rechercher
    </Button>
  )

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const values = form.state.values
        const departureDate = values.flight[0]?.date?.from

        // Check 1: Departure within 5 days — blocking modal
        if (departureDate && isBefore(departureDate, addDays(startOfDay(new Date()), 5))) {
          setIsDateWarningModalOpen(true)
          return
        }

        // Check 2: Round-trip with Réunion — territorial continuity modal
        // if (values.type === 'round-trip' && values.travelClass === 'ECONOMY') {
        //   const hasReunionToMetropolitanFranceSegment = values.flight.some(
        //     (leg) =>
        //       leg.departure.value === REUNION_IATA_CODE &&
        //       METROPOLITAN_FRANCE_IATA_CODES.has(leg.destination.value)
        //   )
        //   if (hasReunionToMetropolitanFranceSegment) {
        //     setIsContinuityModalOpen(true)
        //     return
        //   }
        // }

        void form.handleSubmit()
      }}
    >
      {isMobile ? (
        <div className="flex flex-1 flex-col gap-5">
          {/* Trip type — segmented control */}
          <form.AppField name="type">
            {(field) => (
              <div className="border-input bg-muted/40 flex w-full rounded-xl border p-1">
                {Object.entries(flightSearchEnum).map(([key, label]) => {
                  const active = field.state.value === key
                  return (
                    <button
                      className={cn(
                        'flex-1 rounded-lg px-1 py-2 text-center text-sm font-medium whitespace-nowrap transition-colors',
                        active ? 'bg-brand-600 text-white shadow-sm' : 'text-gray-light-700'
                      )}
                      key={key}
                      onClick={() => field.handleChange(key as FlightSearchSchemaType['type'])}
                      type="button"
                    >
                      {MOBILE_TYPE_LABELS[key] ?? label}
                    </button>
                  )
                })}
              </div>
            )}
          </form.AppField>

          <form.Subscribe selector={(state) => state.values.type}>
            {(type) => (
              <div className="flex flex-col gap-4">
                {legsArray(type)}
                {type === 'multi-destination' && (
                  <Button
                    className="w-full justify-center"
                    onClick={pushFlight}
                    type="button"
                    variant="outline"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="text-sm font-medium">Ajouter un vol</span>
                  </Button>
                )}
              </div>
            )}
          </form.Subscribe>

          {/* Voyageurs · Classe */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1 [&>button]:h-14 [&>button]:w-full">
              <span className={FIELD_LABEL}>Voyageurs</span>
              {passengersField}
            </div>
            <div className="flex flex-col gap-1 [&>div>button]:h-14">
              <span className={FIELD_LABEL}>Classe</span>
              {travelClassField}
            </div>
          </div>

          {/* Bagages */}
          <div className="border-input flex items-center rounded-xl border px-4 py-3">
            <form.AppField name="withLuggage">
              {(field) => (
                <field.SwitchField
                  containerClassName="w-full items-center"
                  label="Bagages en soute inclus"
                />
              )}
            </form.AppField>
          </div>

          {searchButton}
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-4">
          {/* Type of search selection */}
          <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-6">
            <form.AppField
              children={(field) => (
                <field.RadioGroupField
                  options={Object.entries(flightSearchEnum).map(([key, value]) => ({
                    label: value,
                    value: key,
                  }))}
                  orientation="horizontal"
                />
              )}
              name="type"
            />
            <div className="flex flex-row gap-2">
              {passengersField}
              {travelClassField}
            </div>
          </div>

          <div className={cn('flex flex-col flex-wrap items-center gap-3')}>
            <form.Subscribe
              selector={(state) => state.values.type}
              children={(type) => (
                <>
                  {legsArray(type)}
                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                      className={cn('transition-all duration-200', {
                        'opacity-0': type !== 'multi-destination',
                      })}
                      onClick={() => {
                        if (form.state.values.type === 'multi-destination') {
                          pushFlight()
                        }
                      }}
                      type="button"
                    >
                      <Plus className="h-5 w-5" />
                      <p className="text-sm font-medium">Ajouter un vol</p>
                    </Button>

                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                      <form.AppField
                        children={(field) => (
                          <field.SwitchField
                            containerClassName="flex-row-reverse items-center gap-2"
                            label="Bagages en soute inclus"
                          />
                        )}
                        name="withLuggage"
                      />

                      <Button
                        className="bg-brand-600 hover:bg-brand-800 w-full justify-center text-white sm:w-fit"
                        type="submit"
                      >
                        <Search className="size-4" />
                        Rechercher
                      </Button>
                    </div>
                  </div>
                </>
              )}
            />
          </div>
        </div>
      )}

      <RoundTripContinuityModal
        isModalOpen={false}
        onSubmit={() => void form.handleSubmit()}
        setIsModalOpen={() => {}}
        useWordpressDialog={useWordpressDialog}
      />
      <FlightDateWarningModal
        isModalOpen={isDateWarningModalOpen}
        setIsModalOpen={setIsDateWarningModalOpen}
        useWordpressDialog={useWordpressDialog}
      />
    </form>
  )
}
